import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";

const APPROACH_IMG =
    "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc4Nzk5NjA2Mnww&ixlib=rb-4.1.0&q=85";

const PILLARS = [
    "Learning",
    "Collaboration",
    "Project Delivery",
    "Innovation",
    "Performance & Recognition",
    "Engagement & Support",
];

export const Approach = () => {
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

    return (
        <section id="approach" data-testid="approach-section" className="bg-white py-24 lg:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
                    <div>
                        <Reveal>
                            <p className="flex items-center gap-4 text-xs font-bold uppercase tracking-[0.3em] text-[#1FA187]">
                                <span className="h-px w-10 bg-[#1FA187]" />
                                The approach
                            </p>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="font-display mt-6 text-4xl font-bold tracking-tight text-[#0A1B3F] sm:text-5xl">
                                Culture isn't a poster.
                                <br />
                                It's a practice.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-8 text-base leading-relaxed text-slate-600 lg:text-lg">
                                We built EML Synergy after years of watching training decks live and die on
                                slide 42. The platform closes the loop between the behaviour we ask of teams
                                and the behaviour they actually show up with — through short modules, honest
                                challenges, structured 1:1s, and a live score that leaders can't ignore.
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-slate-600 lg:text-lg">
                                Everything is multi-tenant from day one, RBAC-guarded on every endpoint, and
                                audit-logged so enterprise compliance is a one-click export away.
                            </p>
                        </Reveal>
                    </div>
                    <ol data-testid="pillar-list" className="flex flex-col justify-center">
                        {PILLARS.map((pillar, i) => (
                            <motion.li
                                key={pillar}
                                data-testid={`pillar-item-${String(i + 1).padStart(2, "0")}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                                className="group flex items-baseline gap-6 border-b border-slate-200 py-5 transition-colors duration-300 hover:border-[#1E3A8A]"
                            >
                                <span className="font-display text-outline text-4xl font-bold lg:text-5xl">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="font-display text-xl font-semibold tracking-tight text-[#0A1B3F] transition-transform duration-300 group-hover:translate-x-2 lg:text-2xl">
                                    {pillar}
                                </span>
                            </motion.li>
                        ))}
                    </ol>
                </div>

                <Reveal className="mt-20 lg:mt-28">
                    <div
                        ref={imgRef}
                        data-testid="approach-image"
                        className="relative h-[280px] overflow-hidden md:h-[420px]"
                    >
                        <motion.img
                            style={{ y }}
                            src={APPROACH_IMG}
                            alt="Team working together in a modern office"
                            className="absolute inset-0 h-[124%] w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[#0A1B3F]/25" />
                        <p className="font-display absolute bottom-6 left-6 text-sm font-semibold uppercase tracking-[0.25em] text-white md:left-10">
                            Pillar by pillar — week by week
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
